(() => {
    'use strict';

    const hints = Array.from(document.querySelectorAll('[data-sb-region-stock]'));
    const summaries = Array.from(document.querySelectorAll('[data-sb-stock-summary]'));
    if (!hints.length && !summaries.length) return;
    const nativeStock = readJson(document.querySelector('[data-sb-cart-stock]'))?.stock;
    const nativeExpires = Date.now() + 3600000;
    let cartItems = null;

    function readJson(element) {
        try {
            return JSON.parse(element?.textContent || 'null');
        } catch (_) {
            return null;
        }
    }

    function readTime(value) {
        if (typeof value !== 'string' || value.trim() !== value) return NaN;
        const parts = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d{1,6})?(?:Z|[+-]\d{2}:\d{2})$/);
        if (!parts) return NaN;
        const [year, month, day, hour, minute, second] = parts.slice(1).map(Number);
        const calendar = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
        if (calendar.getUTCFullYear() !== year || calendar.getUTCMonth() !== month - 1 || calendar.getUTCDate() !== day
            || calendar.getUTCHours() !== hour || calendar.getUTCMinutes() !== minute || calendar.getUTCSeconds() !== second) return NaN;
        return Date.parse(value.replace(/(\.\d{3})\d+(?=Z|[+-])/, '$1'));
    }

    function observation(snapshot) {
        if (!snapshot || snapshot.version !== 1 || snapshot.tracked !== true || snapshot.complete !== true
            || snapshot.inventory_policy !== 'DENY' || !Array.isArray(snapshot.locations) || !snapshot.locations.length) return null;
        const updated = readTime(snapshot.updated_at);
        const expires = readTime(snapshot.expires_at);
        const now = Date.now();
        if (!Number.isFinite(updated) || !Number.isFinite(expires) || updated > now || now >= expires
            || expires > updated + 3600000) return null;
        const ids = new Set();
        const counts = {};
        for (const location of snapshot.locations) {
            if (!location || typeof location.location_id !== 'string' || !/^[1-9][0-9]*$/.test(location.location_id)
                || location.location_id.trim() !== location.location_id || ids.has(location.location_id)
                || typeof location.country !== 'string' || location.country.length !== 2 || !/^[A-Z]{2}$/.test(location.country)
                || !Number.isSafeInteger(location.available) || location.available < 0) return null;
            ids.add(location.location_id);
            counts[location.country] = (counts[location.country] || 0) + location.available;
            if (!Number.isSafeInteger(counts[location.country])) return null;
        }
        if (counts.CA === undefined || counts.US === undefined) return null;
        return {counts, expires};
    }

    function demand(form, variantId) {
        if (!Array.isArray(cartItems)) return null;
        const quantity = form?.querySelector('[name="quantity"]')?.value ?? '1';
        if (!/^[1-9][0-9]*$/.test(quantity)) return null;
        let total = Number(quantity);
        for (const item of cartItems) {
            if (!item || !Number.isSafeInteger(item.quantity) || item.quantity < 0) return null;
            if (String(item.variant_id) === String(variantId)) total += item.quantity;
        }
        return Number.isSafeInteger(total) ? total : null;
    }

    function countryLabel(hint, country, text, flagAfter = false) {
        const label = document.createElement('span');
        label.className = 'sb-region-stock__country';
        label.textContent = text;
        const flagUrl = country === 'CA' ? hint.dataset.sbCaFlag : country === 'US' ? hint.dataset.sbUsFlag : null;
        if (flagUrl) {
            const flag = document.createElement('img');
            flag.className = 'sb-region-stock__flag';
            flag.src = flagUrl;
            flag.alt = '';
            flag.width = 20;
            flag.height = 14;
            if (flagAfter) label.append(flag);
            else label.prepend(flag);
        }
        return label;
    }

    function refreshSummary(summary) {
        clearTimeout(summary.sbExpiryTimer);
        summary.hidden = true;
        summary.textContent = '';
        const scope = summary.closest('.product-single__meta');
        const form = scope?.querySelector('[data-product-form]');
        const variantId = form?.querySelector('[name="id"]')?.value || summary.dataset.sbSelectedVariant;
        const entry = nativeStock?.[variantId];
        const nativeQuantity = Date.now() < nativeExpires && entry && Number.isSafeInteger(entry.quantity) && entry.quantity >= 0
            ? (entry.available === false ? 0 : entry.quantity) : null;
        const data = form?.querySelector('[data-sb-region-stock-data]');
        const snapshots = readJson(data);
        const snapshot = snapshots && typeof snapshots === 'object' && !Array.isArray(snapshots) ? snapshots[variantId] : null;
        let stock = observation(snapshot);
        // Shopify's native total is independent of the country observation.
        // Unreliable country data must not override it or imply an origin.
        let quantity = nativeQuantity;
        if (stock) {
            const observed = Object.values(stock.counts).reduce((total, count) => total + count, 0);
            if (!Number.isSafeInteger(observed) || (nativeQuantity !== null && nativeQuantity !== observed)) stock = null;
            else quantity = observed;
        }
        if (quantity === null || quantity <= 0) return;
        const expires = stock ? Math.min(stock.expires, nativeQuantity === null ? stock.expires : nativeExpires) : nativeExpires;
        summary.sbExpiryTimer = setTimeout(() => refreshSummary(summary), expires - Date.now());
        const title = document.createElement('p');
        title.className = 'sb-stock-summary__title';
        const copies = `${quantity} ${quantity === 1 ? 'copy' : 'copies'}`;
        title.textContent = quantity <= 5 ? `Only ${copies} left` : `${copies} available`;
        summary.append(title);
        if (summary.hasAttribute('data-sb-condition-stock')) {
            const condition = document.createElement('p');
            condition.className = 'sb-stock-summary__detail';
            condition.textContent = 'In this condition';
            summary.append(condition);
        }
        if (stock) {
            const countries = Object.entries(stock.counts).filter(([, count]) => count > 0);
            const selectedCountry = summary.dataset.sbCountry;
            const names = {CA: 'Canada', US: 'USA'};
            if (countries.length > 1) {
                const split = document.createElement('div');
                split.className = 'sb-stock-summary__countries';
                for (const [code, count] of countries) {
                    split.append(countryLabel(summary, code, `${names[code] || code}: ${count}`));
                }
                summary.append(split);
            } else if (countries.length === 1 && countries[0][0] !== selectedCountry) {
                const code = countries[0][0];
                summary.append(countryLabel(summary, code, `Stock in ${names[code] || code}`));
                if (selectedCountry === 'CA' || selectedCountry === 'US') {
                    const local = document.createElement('p');
                    local.className = 'sb-stock-summary__detail';
                    local.textContent = `No stock currently in ${selectedCountry === 'US' ? 'the USA' : 'Canada'}.`;
                    summary.append(local);
                }
            }
        }
        summary.hidden = false;
    }

    function showStock(hint, stock, country) {
        const other = country === 'CA' ? 'US' : 'CA';
        const names = {CA: 'Canada', US: 'the USA'};
        const message = document.createElement('p');
        message.className = 'sb-region-stock__message';
        // Report observed stock only; a snapshot does not confirm fulfillment allocation.
        if ((hint.dataset.sbCountry === 'CA' || hint.dataset.sbCountry === 'US')
            && stock.counts[country] === 0 && stock.counts[other] > 0
            && Object.entries(stock.counts).every(([code, count]) => code === other || count === 0)) {
            message.textContent = `No stock remaining in ${names[country]}.`;
            const origin = countryLabel(hint, other, `Stock currently available in ${names[other]}`, true);
            origin.classList.add('sb-region-stock__origin');
            hint.append(message, origin);
        } else if (stock.counts.CA === 0 && stock.counts.US === 0) {
            message.textContent = 'No stock remaining in Canada or the USA.';
            hint.append(message);
        } else {
            message.textContent = 'Available stock';
            const countries = document.createElement('div');
            countries.className = 'sb-region-stock__countries';
            for (const code of ['CA', 'US']) {
                const count = stock.counts[code];
                const name = code === 'CA' ? 'Canada' : 'USA';
                countries.append(countryLabel(hint, code, `${name}: ${count} ${count === 1 ? 'copy' : 'copies'}`));
            }
            hint.append(message, countries);
        }
        const confirmation = document.createElement('p');
        confirmation.className = 'sb-region-stock__message';
        confirmation.textContent = 'Orders may ship in separate packages.';
        hint.append(confirmation);
        hint.hidden = false;
    }

    function refresh(hint) {
        clearTimeout(hint.sbExpiryTimer);
        hint.hidden = true;
        hint.textContent = '';
        const form = hint.closest('[data-product-form], .formQuickview');
        const variantId = form?.querySelector('[name="id"]')?.value || hint.dataset.sbSelectedVariant;
        const selectedCountry = hint.dataset.sbCountry;
        if (typeof selectedCountry !== 'string' || !/^[A-Z]{2}$/.test(selectedCountry)) return;
        // This is a display preference, not a routing instruction to Shopify.
        const country = selectedCountry === 'CA' ? 'CA' : 'US';
        const snapshots = readJson(form?.querySelector('[data-sb-region-stock-data]'));
        const stock = observation(snapshots?.[variantId]);
        const requested = demand(form, variantId);
        if (!stock || (requested === null && stock.counts[country] > 0)) return;
        hint.sbExpiryTimer = setTimeout(() => refresh(hint), stock.expires - Date.now());
        // Shopify may clamp the selector to zero before this listener runs.
        // Known local zero still warrants a note about the other warehouse.
        if (stock.counts[country] > 0 && requested <= stock.counts[country]) return;
        showStock(hint, stock, country);
    }

    function refreshAll() {
        hints.forEach(refresh);
        summaries.forEach(refreshSummary);
    }

    cartItems = readJson(document.querySelector('[data-sb-cart-stock]'))?.items;
    document.addEventListener('sb:variant-change', event => {
        hints.filter(hint => event.target.contains(hint)).forEach(refresh);
        summaries.filter(summary => event.target.contains(summary)).forEach(refreshSummary);
    });
    for (const name of ['input', 'change']) {
        document.addEventListener(name, event => {
            if (event.target.matches('[name="quantity"]')) refreshAll();
        });
    }
    if (window.jQuery) {
        window.jQuery(document).on('ajaxCart.afterCartLoad', (_, cart) => {
            cartItems = cart?.items;
            refreshAll();
        });
    }
    document.addEventListener('visibilitychange', refreshAll);
    window.addEventListener('pageshow', refreshAll);
    refreshAll();
})();
